/**
 * OAuth reconnect-required interceptor (frontend)
 *
 * Recognises the backend's 409 reconnect signal so a mutation's `onError` can
 * open the matching Reconnect popup instead of dumping a raw provider string
 * into a toast.
 *
 * Backend contract (apps/backend/src/_common/filters/oauth-reconnect-required.filter.ts):
 *   409 { error: "OAUTH_RECONNECTION_REQUIRED", service, message }
 *
 * Usage in a mutation catch:
 *   const reconnect = getOAuthReconnectError(error)
 *   if (reconnect) {
 *     openPopup(reconnect.service)   // existing useOAuthPopup entry point
 *     return                          // the popup IS the UX — no error toast
 *   }
 *   toast.error(...)                  // fall through for everything else
 */

import { type DbEnum } from "@nexus/types"
// 🚨 `isAxiosError` COMES FROM axios HERE, NOT FROM `./request`, AND THAT IS
// LOAD-BEARING RATHER THAN A STYLE CHOICE. This module is EAGER — the entry
// reaches it through `common/utils/error-handler` — while `./request` is pinned
// into the `api-client` manual chunk beside all 106 generated domain clients
// (`vite.config.ts`). Importing one type guard from it made that whole ~592 kB
// chunk a static dependency of the entry, so every visitor of the public
// `/embed/:embed-id` widget downloaded the console's API client in order to call
// `axios.isAxiosError`. `request.ts`'s copy is `axios.isAxiosError` verbatim, and
// the eager sibling `common/utils/error-handler.ts` already imports it this way.
// Lazy modules may keep importing it from `./request`; only the eager ones cost.
import { isAxiosError } from "axios"

/** Stable backend code for the 409 reconnect-required signal. */
export const OAUTH_RECONNECTION_REQUIRED_CODE = "OAUTH_RECONNECTION_REQUIRED"

/**
 * Parsed reconnect-required error. `service` keys the Reconnect popup; `message`
 * is the backend's user-safe copy (raw provider detail stays server-side).
 */
export interface OAuthReconnectError {
  service: DbEnum.OAuthService
  message: string
}

/**
 * Returns the parsed reconnect signal if `error` is the backend's 409
 * OAUTH_RECONNECTION_REQUIRED response, otherwise `null`.
 */
export function getOAuthReconnectError(error: unknown): OAuthReconnectError | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null

  const data = error.response.data
  if (typeof data !== "object" || data === null) return null

  const body = data as { error?: unknown; service?: unknown; message?: unknown }
  if (body.error !== OAUTH_RECONNECTION_REQUIRED_CODE || typeof body.service !== "string")
    return null

  return {
    service: body.service as DbEnum.OAuthService,
    message: typeof body.message === "string" ? body.message : "Please reconnect your account."
  }
}
